import { createSlice } from "@reduxjs/toolkit";

import {
  createConfiguration,
  deleteConfiguration,
  fetchClinicalDashboardData,
  fetchConfigurationDetail,
  fetchConfigurationList,
  fetchDashboardData,
  updateConfiguration,
} from "redux/actions/psc/pscData";

const providerScorecardConfigInitialState = {
  configuration_id: "",
  page_clinical_shown: false,
  cl_readdressed_card_shown: false,
  cl_readdressed_target_shown: false,
  cl_readdressed_org_avg_shown: false,
  cl_readdressed_dt_avg_shown: false,
  cl_readdressed_target_data: 0,
  cl_suspect_card_shown: false,
  cl_suspect_tier_1_target_shown: false,
  cl_suspect_tier_1_org_avg_shown: false,
  cl_suspect_tier_1_dt_avg_shown: false,
  cl_suspect_tier_1_target_data: 0,
  cl_suspect_tier_2_target_shown: false,
  cl_suspect_tier_2_org_avg_shown: false,
  cl_suspect_tier_2_dt_avg_shown: false,
  cl_suspect_tier_2_target_data: 0,
  cl_awv_card_shown: false,
  cl_awv_target_shown: false,
  cl_awv_org_avg_shown: false,
  cl_awv_dt_avg_shown: false,
  cl_awv_target_data: 0,
  cl_raf_card_shown: false,
  cl_raf_potential_raf_shown: false,
  cl_clinical_report_shown: false,
  cl_clinical_users_report_shown: false,
  page_training_shown: false,
  tr_overall_training_shown: false,
  tr_weekly_participation_shown: false,
  tr_weekly_participation_target_shown: false,
  tr_weekly_participation_target_data: 0,
  tr_question_accuracy_shown: false,
  tr_question_accuracy_target_shown: false,
  tr_question_accuracy_target_data: 0,
  tr_total_question_answered_shown: false,
  tr_total_question_answered_target_shown: false,
  tr_total_question_answered_target_data: 0,
  tr_video_watched_shown: false,
  tr_video_watched_target_shown: false,
  tr_video_watched_target_data: 0,
  tr_articles_read_shown: false,
  tr_articles_read_target_shown: false,
  tr_articles_read_target_data: 0,
  tr_annual_prog_accomplished_shown: false,
  tr_programs_read_target_shown: false,
  tr_annual_prog_accomplished_target: 0,
  tr_annual_training_completion_shown: false,
  tr_learning_stats_shown: false,
  tr_question_answer_full_card_shown: false,
  tr_question_answer_monthly_change_shown: false,
  tr_org_average_card_shown: false,
  tr_org_average_monthly_change_shown: false,
  tr_question_accuracy_card_shown: false,
  tr_question_accuracy_card_monthly_change_shown: false,
  tr_learning_statistics_graph_org_average_shown: false,
  tr_learning_statistics_graph_your_score_shown: false,
  tr_learning_statistics_graph_target_shown: false,
  tr_learning_statistics_graph_target: 0,
  tr_learning_statistics_strong_training_category_shown: false,
  tr_learning_statistics_strong_user_prevalence_shown: false,
  tr_learning_statistics_strong_org_prevalence_shown: false,
  tr_learning_statistics_weak_training_category_shown: false,
  tr_learning_statistics_weak_user_prevalence_shown: false,
  tr_learning_statistics_weak_org_prevalence_shown: false,
  page_panel_shown: false,
  user_monthly_target_attainment: 1,
  show_org_clinic_target_goal: false,
  annual_target_goal: 0,
  org_monthly_target_attainment: 1,
  clinic_level_compliance: false,
  clinic_annual_target_goal: 0,
  clinic_monthly_target_attainment: 1,
  start_date: null,
  end_date: null,
};

const initialState = {
  // UI states
  targetDashboardActiveTab: 0,
  selectedPeriod: "current_month",
  periodValues: [
    "current_month",
    "last_month",
    "current_quarter",
    "last_quarter",
    "current_year",
  ],
  periodTxt: {
    current_month: "This Month",
    last_month: "Last Month",
    current_quarter: "Current Quarter",
    last_quarter: "Last Quarter",
    current_year: "This Year",
  },
  selectedReportVersion: 24,
  reportVersionValues: [24, 28],
  pscSkeletonData: [
    {
      height: 40,
      marginBottom: "10px",
    },
    {
      height: 100,
      marginBottom: "8px",
    },
    {
      height: 100,
      marginBottom: "8px",
    },
    {
      height: 100,
      marginBottom: "8px",
    },
    {
      height: 100,
      marginBottom: "8px",
    },
  ],
  // Dashboard state
  targetDashboardLoading: true,
  targetDashboardData: {
    contest_participation: [],
    question_accuracy: 0,
    questions_answered: 0,
    videos_watched: 0,
    articles_read: 0,
    total_users: 0,
    total_users_completed_certification: 0,
    missing_training_targets: 0,
    programs_completion_count: 0,
    strongest_training_category: [],
    weakest_training_category: [],
  },
  targetDashboardActiveContest: null,
  targetDashboardActiveContestProgress: 0,
  trEmptyData: {
    title: "",
    type: "",
  },
  targetClinicalDashboardLoading: true,
  targetClinicalDashboardData: {
    version: 24,
    recapture_accuracy: 0,
    total_recaptures: 0,
    suspect_accuracy: 0,
    suspect_tier_1_accuracy: 0,
    suspect_tier_2_accuracy: 0,
    total_suspects: 0,
    total_suspects_tier_1: 0,
    total_suspects_tier_2: 0,
    annual_wellness_visits: 0,
    overall_engagement: [],
    missing_clinical_targets: 0,
  },
  targetClinicalDashboardActiveEngage: null,
  targetClinicalDashboardActiveEngageProgress: 0,
  targetClinicalEmptyData: {
    title: "",
    type: "",
  },
  // Configurations
  activeConfiguration: {},
  configurationFormErrValidate: false,
  configurationsListLoading: true,
  configurationsList: [],
  configurationsListSortOrderBy: "",
  configurationsListCount: 0,
  configurationsListPage: 1,
  configurationsListPageSize: 10,
  configDetailLoading: false,
  configEmptyData: {
    title: "",
    type: "",
  },
  // Create Configuration
  newConfigurationCreating: false,
  configurationUpdating: false,
  configurationDeleting: false,
  configurationCreateStartDate: null,
  configurationCreateEndDate: null,
  configurationCreate: {},
  providerScorecardConfig: providerScorecardConfigInitialState,
};

export const pscSlice = createSlice({
  name: "psc",
  initialState,
  reducers: {
    // Common update
    setPscSliceData: (state, { payload }) => {
      if (payload.type && payload.type in state) {
        state[payload.type] = payload.data;
      }
    },
    resetConfigurationCreateData: (state) => {
      state.configurationCreateStartDate = null;
      state.configurationCreateEndDate = null;
      state.providerScorecardConfig = providerScorecardConfigInitialState;
    },
    setPSCConfigurationData: (state, { payload }) => {
      Object.keys(payload).forEach((key) => {
        if (key in state.providerScorecardConfig) {
          state.providerScorecardConfig[key] = payload[key];
        }
      });
    },
  },
  extraReducers: {
    [fetchConfigurationList.pending]: (state) => {
      state.configurationsListLoading = true;
      state.configurationsList = [];
      state.configurationsListCount = 0;
    },
    [fetchConfigurationList.fulfilled]: (state, { payload }) => {
      state.activeConfiguration = {};
      state.configurationsList = payload?.results;
      if (payload?.results?.length > 0) {
        payload.results.forEach(function (configItem) {
          if (configItem?.is_active) {
            state.activeConfiguration = configItem;
            if (state.activeConfiguration?.page_training_shown) {
              const searchParams = new URLSearchParams(location.search);
              const tab = Number(searchParams.get("tab"));
              if (tab === 2) {
                state.targetDashboardActiveTab = 2;
              } else {
                state.targetDashboardActiveTab = 0;
              }
            } else if (state.activeConfiguration?.page_clinical_shown) {
              const searchParams = new URLSearchParams(location.search);
              const tab = Number(searchParams.get("tab"));
              if (tab === 2) {
                state.targetDashboardActiveTab = 2;
              } else {
                state.targetDashboardActiveTab = 1;
              }
            } else {
              state.targetDashboardActiveTab = 2;
            }
          } else {
            state.targetDashboardActiveTab = 2;
          }
        });
      } else {
        state.targetDashboardActiveTab = 2;
      }
      state.configurationsListCount = payload?.count ? payload.count : 0;
      state.configurationsListLoading = false;
    },
    [fetchConfigurationList.rejected]: (state) => {
      state.configurationsListLoading = false;
    },
    [fetchDashboardData.pending]: (state) => {
      state.targetDashboardLoading = true;
    },
    [fetchDashboardData.fulfilled]: (state, { payload }) => {
      state.targetDashboardData.contest_participation =
        payload?.contest_participation ? payload.contest_participation : [];
      state.targetDashboardActiveContestProgress =
        payload?.contest_participation?.length > 0 &&
        payload.contest_participation[0].team_progress
          ? payload.contest_participation[0].team_progress
          : 0;
      state.targetDashboardActiveContest =
        payload?.contest_participation?.length > 0 &&
        payload.contest_participation[0].valid_datetime
          ? payload.contest_participation[0].valid_datetime
          : null;
      state.targetDashboardData.question_accuracy = payload?.question_accuracy
        ? payload.question_accuracy
        : 0;
      state.targetDashboardData.questions_answered = payload?.questions_answered
        ? payload.questions_answered
        : 0;
      state.targetDashboardData.videos_watched = payload?.videos_watched
        ? payload.videos_watched
        : 0;
      state.targetDashboardData.articles_read = payload?.articles_read
        ? payload.articles_read
        : 0;
      state.targetDashboardData.total_users = payload?.total_users
        ? payload.total_users
        : 0;
      state.targetDashboardData.total_users_completed_certification =
        payload?.total_users_completed_certification
          ? payload.total_users_completed_certification
          : 0;
      state.targetDashboardData.missing_training_targets =
        payload?.missing_training_targets
          ? payload.missing_training_targets
          : 0;
      state.targetDashboardData.programs_completion_count =
        payload?.programs_completion_count
          ? payload.programs_completion_count
          : 0;
      state.targetDashboardData.strongest_training_category =
        payload?.strongest_training_category
          ? payload.strongest_training_category
          : [];
      state.targetDashboardData.weakest_training_category =
        payload?.weakest_training_category
          ? payload.weakest_training_category
          : [];
      state.targetDashboardLoading = false;
    },
    [fetchDashboardData.rejected]: (state) => {
      state.targetClinicalDashboardLoading = false;
    },
    [fetchClinicalDashboardData.pending]: (state) => {
      state.targetClinicalDashboardLoading = true;
    },
    [fetchClinicalDashboardData.fulfilled]: (state, { payload }) => {
      state.targetClinicalDashboardData.overall_engagement =
        payload?.overall_engagement ? payload.overall_engagement : [];
      state.targetClinicalDashboardActiveEngageProgress =
        payload?.overall_engagement?.length > 0 &&
        payload.overall_engagement[0].value
          ? payload.overall_engagement[0].value
          : 0;
      state.targetClinicalDashboardActiveEngage =
        payload?.overall_engagement?.length > 0 &&
        payload.overall_engagement[0].created_on
          ? payload.overall_engagement[0].created_on
          : null;
      state.targetClinicalDashboardData.recapture_accuracy =
        payload?.recapture_accuracy ? payload.recapture_accuracy : 0;
      state.targetClinicalDashboardData.total_recaptures =
        payload?.total_recaptures ? payload.total_recaptures : 0;
      state.targetClinicalDashboardData.suspect_accuracy =
        payload?.suspect_accuracy ? payload.suspect_accuracy : 0;
      state.targetClinicalDashboardData.suspect_tier_1_accuracy =
        payload?.suspect_tier_1_accuracy ? payload.suspect_tier_1_accuracy : 0;
      state.targetClinicalDashboardData.suspect_tier_2_accuracy =
        payload?.suspect_tier_2_accuracy ? payload.suspect_tier_2_accuracy : 0;
      state.targetClinicalDashboardData.total_suspects = payload?.total_suspects
        ? payload.total_suspects
        : 0;
      state.targetClinicalDashboardData.total_suspects_tier_1 =
        payload?.total_suspects_tier_1 ? payload.total_suspects_tier_1 : 0;
      state.targetClinicalDashboardData.total_suspects_tier_2 =
        payload?.total_suspects_tier_2 ? payload.total_suspects_tier_2 : 0;
      state.targetClinicalDashboardData.annual_wellness_visits =
        payload?.annual_wellness_visits ? payload.annual_wellness_visits : 0;
      state.targetClinicalDashboardData.missing_clinical_targets =
        payload?.missing_clinical_targets
          ? payload.missing_clinical_targets
          : 0;
      state.targetClinicalDashboardLoading = false;
    },
    [fetchClinicalDashboardData.rejected]: (state) => {
      state.targetClinicalDashboardLoading = false;
    },
    [createConfiguration.pending]: (state) => {
      state.newConfigurationCreating = true;
    },
    [createConfiguration.fulfilled]: (state) => {
      state.newConfigurationCreating = false;
    },
    [createConfiguration.rejected]: (state) => {
      state.newConfigurationCreating = false;
    },
    [updateConfiguration.pending]: (state) => {
      state.configurationUpdating = true;
    },
    [updateConfiguration.fulfilled]: (state) => {
      state.configurationUpdating = false;
    },
    [updateConfiguration.rejected]: (state) => {
      state.configurationUpdating = false;
    },
    [deleteConfiguration.pending]: (state) => {
      state.configurationDeleting = true;
    },
    [deleteConfiguration.fulfilled]: (state) => {
      state.configurationDeleting = false;
    },
    [deleteConfiguration.rejected]: (state) => {
      state.configurationDeleting = false;
    },
    [fetchConfigurationDetail.pending]: (state) => {
      state.configDetailLoading = true;
    },
    [fetchConfigurationDetail.fulfilled]: (state, { payload }) => {
      // Set training data
      state.providerScorecardConfig = payload;
      state.configDetailLoading = false;
    },
    [fetchConfigurationDetail.rejected]: (state) => {
      state.configDetailLoading = false;
    },
  },
});

export const {
  setPscSliceData,
  setPSCConfigurationData,
  resetConfigurationCreateData,
} = pscSlice.actions;

export default pscSlice.reducer;
